'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff, Zap, Mail, Phone, ChevronDown, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// Country list with dial codes
const COUNTRIES = [
  { code: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { code: 'AE', name: 'United Arab Emirates', dial: '+971', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '🇸🇦' },
  { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
  { code: 'SG', name: 'Singapore', dial: '+65', flag: '🇸🇬' },
  { code: 'JP', name: 'Japan', dial: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dial: '+86', flag: '🇨🇳' },
  { code: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', dial: '+254', flag: '🇰🇪' },
  { code: 'GH', name: 'Ghana', dial: '+233', flag: '🇬🇭' },
  { code: 'EG', name: 'Egypt', dial: '+20', flag: '🇪🇬' },
  { code: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', dial: '+880', flag: '🇧🇩' },
  { code: 'PH', name: 'Philippines', dial: '+63', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', dial: '+62', flag: '🇮🇩' },
  { code: 'MY', name: 'Malaysia', dial: '+60', flag: '🇲🇾' },
  { code: 'TH', name: 'Thailand', dial: '+66', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', dial: '+84', flag: '🇻🇳' },
  { code: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
  { code: 'TR', name: 'Turkey', dial: '+90', flag: '🇹🇷' },
  { code: 'RU', name: 'Russia', dial: '+7', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', dial: '+380', flag: '🇺🇦' },
  { code: 'PL', name: 'Poland', dial: '+48', flag: '🇵🇱' },
  { code: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱' },
  { code: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
  { code: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
  { code: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭' },
  { code: 'SE', name: 'Sweden', dial: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dial: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dial: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dial: '+358', flag: '🇫🇮' },
  { code: 'NZ', name: 'New Zealand', dial: '+64', flag: '🇳🇿' },
  { code: 'QA', name: 'Qatar', dial: '+974', flag: '🇶🇦' },
  { code: 'KW', name: 'Kuwait', dial: '+965', flag: '🇰🇼' },
  { code: 'BH', name: 'Bahrain', dial: '+973', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', dial: '+968', flag: '🇴🇲' },
  { code: 'JO', name: 'Jordan', dial: '+962', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon', dial: '+961', flag: '🇱🇧' },
  { code: 'MA', name: 'Morocco', dial: '+212', flag: '🇲🇦' },
  { code: 'TN', name: 'Tunisia', dial: '+216', flag: '🇹🇳' },
  { code: 'DZ', name: 'Algeria', dial: '+213', flag: '🇩🇿' },
];

type Step = 'form' | 'success';
type VerifyMethod = 'email' | 'phone';

interface FormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  country: string;
  verifyMethod: VerifyMethod;
}

export default function RegisterPage() {
  const router = useRouter();
  const supabase = createClient();

  const [step, setStep] = useState<Step>('form');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: 'US',
    verifyMethod: 'email',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Country dropdown
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const selectedCountry = COUNTRIES.find((c) => c.code === formData.country) ?? COUNTRIES[0];
  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.dial.includes(countrySearch)
  );

  // Close country dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) { setFormError('Full name is required'); return; }
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      setFormError('A valid email address is required'); return;
    }
    if (formData.verifyMethod === 'phone' && !formData.phone.trim()) {
      setFormError('Phone number is required for phone verification'); return;
    }
    if (formData.password.length < 6) { setFormError('Password must be at least 6 characters'); return; }
    if (formData.password !== formData.confirmPassword) { setFormError('Passwords do not match'); return; }

    setIsLoading(true);

    const { error } = await supabase.auth.signUp({
      email: formData.email.trim(),
      password: formData.password,
      options: {
        emailRedirectTo: window.location.origin + '/auth/callback',
        data: {
          full_name: formData.name.trim(),
          name: formData.name.trim(),
          country: formData.country,
          phone: formData.verifyMethod === 'phone' ? `${selectedCountry.dial}${formData.phone.trim()}` : undefined,
          role: 'client',
        },
      },
    });

    setIsLoading(false);

    if (error) {
      setFormError(error.message);
      return;
    }

    setStep('success');
  };

  const handleGoToDashboard = () => {
    router.push('/client-dashboard');
  };

  const inputCls =
    'w-full px-4 py-3 rounded-lg text-sm text-foreground placeholder-muted-foreground transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary';
  const inputStyle = { background: 'var(--input)', border: '1px solid var(--border)' };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Branding Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-2/5 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: '#000000' }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
          <div className="absolute bottom-40 left-10 w-96 h-96 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #F59E0B, transparent)' }} />
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
            <span className="text-black font-bold text-sm">FX</span>
          </div>
          <div>
            <span className="text-white font-bold text-xl tracking-tight">United</span>
            <span className="text-gradient-gold font-bold text-xl tracking-tight">FXA</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.4)' }}>
            <Zap size={12} />
            Open to Traders Worldwide
          </div>
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
            Start Trading<br />
            <span className="text-gradient-gold">in Minutes</span>
          </h1>
          <p className="text-base max-w-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Create your UnitedFXA account, complete verification, and access global markets with up to 1:400 leverage.
          </p>

          <div className="space-y-3 pt-2">
            {[
              'Available in 50+ countries',
              'KYC verification for account security',
              'Silver, Gold, Platinum & more account types',
              'Instant access after verification',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(245,158,11,0.2)' }}>
                  <CheckCircle size={11} style={{ color: '#F59E0B' }} />
                </div>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          © 2026 UnitedFXA. All rights reserved. Trading involves significant risk of loss.{' '}
          <a href="https://www.unitedfxa.com" target="_blank" rel="noopener noreferrer" style={{ color: '#F59E0B' }} className="hover:underline">
            www.unitedfxa.com
          </a>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
            <span className="text-black font-bold text-sm">FX</span>
          </div>
          <div>
            <span className="text-foreground font-bold text-xl">United</span>
            <span className="text-gradient-gold font-bold text-xl">FXA</span>
          </div>
        </div>

        <div className="w-full max-w-md animate-slide-up">
          {/* ── STEP 1: Registration Form ── */}
          {step === 'form' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
                <p className="text-muted-foreground text-sm">Join UnitedFXA — available worldwide</p>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Full Name */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Full Name</label>
                  <input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Email Address</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>

                {/* Country */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Country</label>
                  <div className="relative" ref={countryDropdownRef}>
                    <button
                      type="button"
                      onClick={() => { setShowCountryDropdown((v) => !v); setCountrySearch(''); }}
                      className="w-full px-4 py-3 rounded-lg text-sm text-foreground flex items-center justify-between transition-all focus:outline-none focus:ring-2 focus:ring-primary"
                      style={inputStyle}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-base">{selectedCountry.flag}</span>
                        <span>{selectedCountry.name}</span>
                        <span className="text-muted-foreground text-xs">{selectedCountry.dial}</span>
                      </span>
                      <ChevronDown size={14} className="text-muted-foreground" />
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute z-50 w-full mt-1 rounded-xl shadow-xl overflow-hidden"
                        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                        <div className="p-2 border-b" style={{ borderColor: 'var(--border)' }}>
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg text-sm text-foreground focus:outline-none"
                            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
                            autoFocus
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCountries.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => {
                                setFormData((f) => ({ ...f, country: c.code }));
                                setShowCountryDropdown(false);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-white/5 transition-colors"
                              style={{ color: formData.country === c.code ? '#F59E0B' : 'var(--foreground)' }}
                            >
                              <span className="text-base">{c.flag}</span>
                              <span className="flex-1">{c.name}</span>
                              <span className="text-xs text-muted-foreground">{c.dial}</span>
                            </button>
                          ))}
                          {filteredCountries.length === 0 && (
                            <p className="px-4 py-3 text-sm text-muted-foreground">No countries found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Verification Method */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Verification Method</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['email', 'phone'] as VerifyMethod[]).map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setFormData((f) => ({ ...f, verifyMethod: method }))}
                        className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all"
                        style={
                          formData.verifyMethod === method
                            ? { background: 'rgba(245,158,11,0.2)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.5)' }
                            : { background: 'var(--input)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }
                        }
                      >
                        {method === 'email' ? <Mail size={14} /> : <Phone size={14} />}
                        {method === 'email' ? 'Email' : 'Phone'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Phone (if phone method) */}
                {formData.verifyMethod === 'phone' && (
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-foreground">Phone Number</label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 py-3 rounded-lg text-sm text-muted-foreground flex-shrink-0"
                        style={inputStyle}>
                        {selectedCountry.flag} {selectedCountry.dial}
                      </div>
                      <input
                        type="tel"
                        placeholder="555 000 0000"
                        value={formData.phone}
                        onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
                        className={`${inputCls} flex-1`}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}

                {/* Password */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
                      className={`${inputCls} pr-11`}
                      style={inputStyle}
                    />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-foreground">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData((f) => ({ ...f, confirmPassword: e.target.value }))}
                      className={`${inputCls} pr-11`}
                      style={inputStyle}
                    />
                    <button type="button" onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {formError && (
                  <div className="px-4 py-3 rounded-lg text-sm"
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-lg font-semibold text-sm text-primary-foreground transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>

                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link href="/" className="font-semibold hover:opacity-80 transition-opacity" style={{ color: '#F59E0B' }}>
                    Sign in
                  </Link>
                </p>
              </form>
            </div>
          )}

          {/* ── STEP 2: Success ── */}
          {step === 'success' && (
            <div className="text-center space-y-6 animate-slide-up">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(16,185,129,0.15)', border: '2px solid rgba(16,185,129,0.4)' }}>
                <CheckCircle size={40} className="text-emerald-400" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-foreground">Account Created!</h2>
                <p className="text-muted-foreground text-sm">
                  Welcome to UnitedFXA, <strong className="text-foreground">{formData.name.split(' ')[0]}</strong>!
                  Your account is active. Complete your KYC verification to unlock all features.
                </p>
              </div>

              <div className="p-4 rounded-xl text-left space-y-2"
                style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">KYC Pending</p>
                <p className="text-sm text-muted-foreground">
                  Your KYC status is <strong className="text-amber-400">Pending</strong>. Until verified by our team, deposit wallet addresses and withdrawals will be restricted.
                </p>
              </div>

              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 rounded-lg font-semibold text-sm text-primary-foreground transition-all duration-150 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
